import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { ChevronRight, Loader2, ExternalLink, RefreshCw, Plus, X } from "lucide-react";
import { toast } from "sonner";
import DemoLayout from "@/components/DemoLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DemoDashboard() {
  const [params] = useSearchParams();
  const personaId = params.get("persona");
  const navigate = useNavigate();

  const [feed, setFeed] = useState(null);
  const [persona, setPersona] = useState(null);
  const [dismissed, setDismissed] = useState({});

  useEffect(() => {
    let alive = true;
    if (!personaId) { navigate("/demo", { replace: true }); return; }
    Promise.all([
      axios.get(`${API}/demo/feed/${personaId}`),
      axios.get(`${API}/demo/profile/${personaId}`),
    ]).then(([f, p]) => {
      if (!alive) return;
      setFeed(f.data);
      setPersona(p.data.profile);
    }).catch(() => navigate("/demo", { replace: true }));
    return () => { alive = false; };
  }, [personaId, navigate]);

  const visibleJobs = useMemo(() => (feed?.jobs || []).filter((e) => !dismissed[e.job.id]), [feed, dismissed]);

  const onDismiss = (jobId, title) => {
    setDismissed((d) => ({ ...d, [jobId]: true }));
    toast(`Removed "${title}" from feed.`, {
      duration: 300000,
      action: {
        label: "Undo",
        onClick: () => {
          setDismissed((d) => { const c = { ...d }; delete c[jobId]; return c; });
          toast.success("Restored to feed.");
        },
      },
    });
  };

  if (!feed || !persona) {
    return <DemoLayout><div className="p-12 text-center text-muted-foreground text-xs uppercase tracking-[0.3em]">LOADING FEED<span className="blink-caret"/></div></DemoLayout>;
  }

  const ready = visibleJobs.filter((x) => x.user_job?.materials_status === "ready");
  const generating = visibleJobs.filter((x) => !x.user_job || x.user_job?.materials_status === "generating");

  return (
    <DemoLayout persona={persona}>
      <div className="max-w-[1600px] mx-auto px-5 md:px-12 py-6 sm:py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6 sm:mb-10">
          <div>
            <div className="terminal-label text-primary">[ DAILY.FEED / {new Date().toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" }).toUpperCase()} ]</div>
            <h1 className="font-display text-2xl sm:text-4xl md:text-5xl uppercase font-bold tracking-tight mt-2">Today&apos;s matches</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-xl">
              {ready.length} ready · {generating.length} generating in background. Highest combined score first.
            </p>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <button onClick={() => toast("Adding custom jobs is disabled in demo mode.")} className="btn-primary inline-flex items-center justify-center gap-2 flex-1 sm:flex-none">
              <Plus className="w-3 h-3" /> Add a job
            </button>
            <button onClick={() => toast.success("Demo feed is static. Sign up for a fresh feed every morning.")} className="btn-secondary inline-flex items-center justify-center gap-2 flex-1 sm:flex-none">
              <RefreshCw className="w-3 h-3" /> Refresh
            </button>
          </div>
        </div>

        <div className="panel p-4 mb-px flex items-center gap-3 flex-wrap">
          <span className="terminal-label">Filter by location:</span>
          <select className="input-base w-auto min-w-[200px] py-2" disabled>
            <option>All locations</option>
          </select>
          <span className="terminal-label ml-auto">{visibleJobs.length} listing{visibleJobs.length === 1 ? "" : "s"}</span>
        </div>

        <div className="border border-foreground/30">
          <div className="hidden md:grid grid-cols-12 gap-px bg-foreground/30 border-b border-foreground/30">
            <div className="col-span-1 bg-background p-3 terminal-label">#</div>
            <div className="col-span-4 bg-background p-3 terminal-label">Role / Company</div>
            <div className="col-span-2 bg-background p-3 terminal-label">Location</div>
            <div className="col-span-2 bg-background p-3 terminal-label">Match</div>
            <div className="col-span-2 bg-background p-3 terminal-label">Enjoyment</div>
            <div className="col-span-1 bg-background p-3 terminal-label text-right">→</div>
          </div>
          {visibleJobs.map((entry, idx) => (
            <FeedRow key={entry.job.id} idx={idx} entry={entry} personaId={personaId}
              onClick={() => navigate(`/demo/applications/${entry.job.id}?persona=${personaId}`)}
              onDismiss={() => onDismiss(entry.job.id, entry.job.title)} />
          ))}
          {visibleJobs.length === 0 && (
            <div className="p-12 text-center">
              <p className="text-sm text-muted-foreground">You dismissed every job. Use undo on the toast or refresh.</p>
            </div>
          )}
        </div>

        <div className="mt-10 p-6 brut-tile text-center" style={{ borderColor: "hsl(var(--primary) / 0.4)" }}>
          <p className="text-sm text-foreground/85 mb-1">This is everything you would see signed in.</p>
          <p className="text-xs text-muted-foreground mb-4">In the real product the feed updates daily with 30 to 80 jobs, every one tailored for you.</p>
          <Link to="/auth?mode=signup" data-testid="demo-cta-signup" className="btn-primary inline-flex items-center gap-2">
            Start your free trial <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </DemoLayout>
  );
}

function ScoreBar({ value, color }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1 bg-surface relative">
        <div className="absolute left-0 top-0 h-full" style={{ width: `${value}%`, backgroundColor: color }} />
      </div>
      <span className="font-mono text-sm tabular-nums w-8 text-right" style={{ color }}>{value}</span>
    </div>
  );
}

function FeedRow({ entry, idx, personaId, onClick, onDismiss }) {
  const { job, user_job } = entry;
  const ready = user_job?.materials_status === "ready";
  const matchColor = "#00FF41";
  const enjoyColor = "#FFB000";
  return (
    <div
      data-testid={`demo-job-${job.id}`}
      onClick={onClick}
      className="md:grid md:grid-cols-12 gap-2 md:gap-px md:bg-border border-b border-border last:border-b-0 cursor-pointer transition-colors group p-4 md:p-0 bg-background hover:bg-surface-hover"
    >
      {/* Mobile */}
      <div className="md:hidden">
        <div className="flex items-center justify-between gap-2 mb-2">
          <span className="font-mono text-xs tabular-nums text-muted-foreground">{String(idx + 1).padStart(3, "0")}</span>
          <div className="flex items-center gap-2">
            <button onClick={(e) => { e.stopPropagation(); onDismiss(); }} className="text-muted-foreground hover:text-destructive p-1"><X className="w-3 h-3" /></button>
            <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary" />
          </div>
        </div>
        <div className="text-base font-display font-medium uppercase tracking-tight break-words">{job.title}</div>
        <div className="text-xs text-muted-foreground mt-0.5 truncate">{job.company} · {job.location}</div>
        {ready && (
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div><div className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground mb-1 font-mono">Match</div><ScoreBar value={user_job.match_score} color={matchColor} /></div>
            <div><div className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground mb-1 font-mono">Enjoyment</div><ScoreBar value={user_job.preference_score} color={enjoyColor} /></div>
          </div>
        )}
      </div>
      {/* Desktop */}
      <div className="hidden md:block md:col-span-1 md:p-4 font-mono text-xs tabular-nums md:bg-background text-muted-foreground">{String(idx + 1).padStart(3, "0")}</div>
      <div className="hidden md:block md:col-span-4 md:p-4 md:bg-background min-w-0">
        <div className="text-base font-display font-medium uppercase tracking-tight break-words">{job.title}</div>
        <div className="text-xs text-muted-foreground mt-0.5 truncate">{job.company}{job.salary ? ` · ${job.salary}` : ""}</div>
      </div>
      <div className="hidden md:flex items-center md:col-span-2 md:p-4 md:bg-background text-xs text-muted-foreground truncate">{job.location || "Remote"}</div>
      <div className="hidden md:flex items-center md:col-span-2 md:p-4 md:bg-background">
        <ScoreBar value={user_job.match_score} color={matchColor} />
      </div>
      <div className="hidden md:flex items-center md:col-span-2 md:p-4 md:bg-background">
        <ScoreBar value={user_job.preference_score} color={enjoyColor} />
      </div>
      <div className="hidden md:flex md:col-span-1 md:p-4 md:bg-background items-center justify-end gap-2">
        {job.url && (
          <a href={job.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-muted-foreground hover:text-primary"><ExternalLink className="w-3 h-3" /></a>
        )}
        <button onClick={(e) => { e.stopPropagation(); onDismiss(); }} title="Not interested" className="text-muted-foreground hover:text-destructive p-1"><X className="w-3 h-3" /></button>
        <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary" />
      </div>
    </div>
  );
}
